import type { CoveredPlatform } from '../../platform/platform.types';
import { BRAND_NAME } from '../../shared/brand';
import { num } from './format';

/**
 * The site's figures, as SiteFactsService reads them and every page, email
 * and the dashboard print them. Pure, so the server's view models, the
 * page scripts and the SPA all word a figure the same way. A figure that
 * could not be read is null, and whatever states it is left out: never a
 * guess, never a 0 it cannot back.
 */

/** A platform the public pages name: one read today, or a planned one. */
export type FactPlatform = Pick<
  CoveredPlatform,
  | 'id'
  | 'name'
  | 'country'
  | 'regimes'
  | 'minTicket'
  | 'minTicketNote'
  | 'currency'
> & { planned: boolean };

export interface SiteFacts {
  /** Live raises now: the feed's raises in scope, as the dashboard counts them. */
  liveCount: number | null;
  /** Platforms whose raises are read today (coverage not PLANNED). */
  platformCount: number | null;
  /** Platforms listed as planned: in the directory, read by nothing yet. */
  plannedCount: number | null;
  /** The countries the platforms read today are in. */
  countryCount: number | null;
  /** New raises a day, by launch date, averaged over the last 30 days. */
  newPerDay: number | null;
  /** Raises launched in the last 365 days, by the same rule as newPerDay. */
  launchedYear: number | null;
  /** SEC EDGAR's Form C filings are read. */
  edgar: boolean | null;
  /** The platforms with the most live raises, busiest first: the sonar's blips. */
  busiest: string[] | null;
  /** Every platform counted above, and the planned ones, by name. */
  platforms: FactPlatform[] | null;
}

/** The days newPerDay averages over, and launchedYear counts. */
export const PER_DAY_DAYS = 30;
export const YEAR_DAYS = 365;

/** The counts from the platform list: what platformCount, plannedCount and countryCount are. */
export function platformCounts(platforms: readonly FactPlatform[]): {
  platformCount: number;
  plannedCount: number;
  countryCount: number;
} {
  const read = platforms.filter((p) => !p.planned);
  const countries = new Set(
    read.flatMap((p) => (p.country ? [p.country] : [])),
  );
  return {
    platformCount: read.length,
    plannedCount: platforms.length - read.length,
    countryCount: countries.size,
  };
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

const counted = (n: number, one: string, many: string) =>
  `${num(n)} ${n === 1 ? one : many}`;

/** A figure that is a whole count, as printed: "1,204". Null stays null. */
export function countLabel(n: number | null): string | null {
  return n === null || !Number.isFinite(n) ? null : num(Math.round(n));
}

/**
 * The 30-day average, to the nearest whole raise: "~3". Under half a raise
 * a day it would print as "~0", so it is left out.
 */
export function perDayLabel(n: number | null): string | null {
  if (n === null || !Number.isFinite(n)) return null;
  const whole = Math.round(n);
  return whole >= 1 ? `~${num(whole)}` : null;
}

/**
 * "26 platforms in 12 countries", with the noun before "platforms" if one is
 * given ("26 equity-crowdfunding platforms in 12 countries"); without the
 * countries where they are unknown; null without the platforms.
 */
export function coverageLine(
  facts: Pick<SiteFacts, 'platformCount' | 'countryCount'>,
  kind = '',
): string | null {
  const { platformCount, countryCount } = facts;
  if (platformCount === null || platformCount < 1) return null;
  const pre = kind ? `${kind} ` : '';
  const platforms = counted(platformCount, `${pre}platform`, `${pre}platforms`);
  return countryCount !== null && countryCount >= 1
    ? `${platforms} in ${counted(countryCount, 'country', 'countries')}`
    : platforms;
}

/**
 * The canonical description, the one sentence that says what Raise Sonar
 * is: "Raise Sonar tracks every live equity crowdfunding raise across 26
 * platforms in 12 countries plus SEC EDGAR, and emails you when a raise
 * opens, nears its cap or is about to close." The home page's meta
 * description and the paragraph under its heading, and the meta description
 * of every page without its own (head.hbs, `facts.description`).
 * marketing/canonical-description.md has it with today's figures, and the
 * short forms for directories and bios. Without the countries it says "26
 * platforms"; without the platforms, "the platforms it reads"; without SEC
 * EDGAR, nothing about it.
 */
export function canonicalDescription(
  facts: Pick<SiteFacts, 'platformCount' | 'countryCount' | 'edgar'>,
): string {
  const across = coverageLine(facts) ?? 'the platforms it reads';
  const edgar = facts.edgar === true ? ' plus SEC EDGAR' : '';
  return `${BRAND_NAME} tracks every live equity crowdfunding raise across ${across}${edgar}, and emails you when a raise opens, nears its cap or is about to close.`;
}

/** "Opstart and Kria", "Opstart, Kria and 5 more": planned platforms, at most three named. */
export function plannedNames(
  platforms: readonly Pick<FactPlatform, 'name' | 'planned'>[],
): string | null {
  const names = platforms.filter((p) => p.planned).map((p) => p.name);
  if (names.length === 0) return null;
  if (names.length <= 3) {
    return names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }
  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
}

/**
 * The facts as every Handlebars page has them, `{{facts.platforms}}` and the
 * like (SiteFactsLocals puts them on res.locals). Each is the printed figure,
 * or null where it is unknown, so a template can `{{#if}}` it away.
 */
export interface FactsView {
  /** "312" */
  liveRaises: string | null;
  /** "26" */
  platforms: string | null;
  /** "7" */
  planned: string | null;
  /** "12" */
  countries: string | null;
  /** "~3" */
  newPerDay: string | null;
  /** "1,204" */
  launchedYear: string | null;
  /** "27": the platforms, and SEC EDGAR where it is read. */
  sources: string | null;
  edgar: boolean;
  /** "26 platforms in 12 countries" */
  coverage: string | null;
  /** The canonical description (canonicalDescription), never empty. */
  description: string;
}

export function factsView(facts: SiteFacts): FactsView {
  const edgar = facts.edgar === true;
  return {
    liveRaises: countLabel(facts.liveCount),
    platforms: countLabel(facts.platformCount),
    planned: countLabel(facts.plannedCount),
    countries: countLabel(facts.countryCount),
    newPerDay: perDayLabel(facts.newPerDay),
    launchedYear: countLabel(facts.launchedYear),
    sources:
      facts.platformCount === null
        ? null
        : num(facts.platformCount + (edgar ? 1 : 0)),
    edgar,
    coverage: coverageLine(facts),
    description: canonicalDescription(facts),
  };
}

// ---------------------------------------------------------------------------
// GET /api/site/facts
// ---------------------------------------------------------------------------

/** GET /api/site/facts: the facts for anyone, the SPA's login page and Data page among them. */
export interface SiteFactsWire {
  /** Platforms whose raises are read today. */
  livePlatforms: number | null;
  /** Platforms listed as planned. */
  plannedPlatforms: number | null;
  /** Countries the live platforms are in. */
  countries: number | null;
  /** Live raises now. */
  liveRaises: number | null;
  /** New raises a day, a 30-day average, to two decimals. */
  newRaisesPerDay: number | null;
  /** Raises launched in the last 365 days. */
  raisesLast365Days: number | null;
  /** SEC EDGAR's Form C filings are read. */
  secEdgar: boolean | null;
}

export function toWire(facts: SiteFacts): SiteFactsWire {
  return {
    livePlatforms: facts.platformCount,
    plannedPlatforms: facts.plannedCount,
    countries: facts.countryCount,
    liveRaises: facts.liveCount,
    newRaisesPerDay:
      facts.newPerDay === null ? null : Math.round(facts.newPerDay * 100) / 100,
    raisesLast365Days: facts.launchedYear,
    secEdgar: facts.edgar,
  };
}

/** The endpoint's answer as SiteFacts (no platform list), or null for anything else. */
export function readWire(body: unknown): SiteFacts | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  const n = (v: unknown) =>
    typeof v === 'number' && Number.isFinite(v) ? v : null;
  return {
    liveCount: n(b.liveRaises),
    platformCount: n(b.livePlatforms),
    plannedCount: n(b.plannedPlatforms),
    countryCount: n(b.countries),
    newPerDay: n(b.newRaisesPerDay),
    launchedYear: n(b.raisesLast365Days),
    edgar: typeof b.secEdgar === 'boolean' ? b.secEdgar : null,
    busiest: null,
    platforms: null,
  };
}
